import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { leadApi } from "api/lead";
import { BaseOption, InputSize, Select } from "shared/components";
import { Avatar } from "shared/components/Avatar";
import { cx } from "shared/utils";
import { useAuthContext } from "providers/Auth";
import { useMemo } from "react";
import { toast } from "react-toastify";
import { User } from "views/settings-users/types";

interface AssigneeSelectProps {
  value?: number | null;
  rowId?: string;
  inputClassName?: string;
  onChange?: (partner_user_id: number | null) => void;
  label?: string;
}

type AssigneeUser = Pick<User, "id" | "name">;
type AssigneeOption = BaseOption & {
  kind: "any" | "user";
  user?: AssigneeUser;
};

export const AssigneeSelect = ({
  value,
  rowId,
  inputClassName,
  onChange,
  label
}: AssigneeSelectProps) => {
  const queryClient = useQueryClient();
  const { auth } = useAuthContext();

  const { data: assignees, isLoading } = useQuery({
    queryKey: ["LEAD_ASSIGNEES"],
    queryFn: () => leadApi.getAssignees()
  });

  const { mutateAsync } = useMutation({
    mutationKey: ["LEAD_ASSIGN"],
    mutationFn: ({
      lead_id,
      partner_user_id
    }: {
      lead_id: string;
      partner_user_id: number | null;
    }) =>
      leadApi.assignLead({
        lead_id: Number(lead_id),
        partner_user_id
      }),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["LEAD_ACTIVITY_LOG"] });
      queryClient.refetchQueries({ queryKey: ["LEADS_TABLE"] });
      if (rowId) queryClient.refetchQueries({ queryKey: ["LEAD", rowId] });
      toast.success("Lead assigned successfully");
    },
    onError: () => {
      toast.error("Failed to assign lead");
    }
  });

  const sortedAssignees = useMemo(() => {
    if (!assignees) return [];
    const sorted = assignees.toSorted((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });
    if (auth) {
      const withoutAuth = sorted.filter((a) => a.id !== auth.id);
      return [auth, ...withoutAuth];
    }
    return sorted;
  }, [assignees, auth]);

  const handleAssigneeChange = (userId: number | null) => {
    if (onChange) onChange(userId);
    else if (rowId)
      mutateAsync({
        lead_id: String(rowId),
        partner_user_id: userId
      });
  };

  const selectValue = useMemo(
    () => value ?? (onChange ? "any" : undefined),
    [onChange, value]
  );

  const options = useMemo<AssigneeOption[]>(() => {
    const baseOptions: AssigneeOption[] = onChange
      ? [{ label: "Any", value: "any", kind: "any" }]
      : [];

    return [
      ...baseOptions,
      ...sortedAssignees.map((assignee) => ({
        label: assignee.name,
        value: assignee.id,
        id: assignee.id,
        kind: "user" as const,
        user: {
          id: assignee.id,
          name: assignee.name
        }
      }))
    ];
  }, [onChange, sortedAssignees]);

  const optionsKey = useMemo(() => {
    return `${options.length}-${auth?.id ?? "no-auth"}-${onChange ? "filter" : "row"}`;
  }, [options, auth, onChange]);

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className={inputClassName || "w-full"}
    >
      {label && (
        <label className="block text-sm font-medium text-content-1000 mb-1">
          {label}
        </label>
      )}
      <Select
        key={optionsKey}
        id={`assigned-to-select-${rowId || "filter"}`}
        size={InputSize.SM}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        className={cx("w-full", inputClassName)}
        value={selectValue}
        options={options}
        isDisabled={isLoading}
        placeholder="Unassigned"
        legacyErrorMargins={false}
        styles={{
          valueContainer: (base) => ({
            ...base,
            alignItems: "center",
            paddingTop: 0,
            paddingBottom: 0
          }),
          singleValue: (base) => ({
            ...base,
            margin: 0,
            maxWidth: "100%",
            display: "flex",
            alignItems: "center"
          })
        }}
        onChange={(e) => {
          const nextValue = e.target.value;
          if (onChange) {
            if (nextValue === "any") return handleAssigneeChange(null);
            return handleAssigneeChange(Number(nextValue));
          }
          handleAssigneeChange(Number(nextValue));
        }}
        formatOptionLabel={(option, meta) => {
          const assigneeOption = option as AssigneeOption;
          if (meta.context === "value") {
            if (assigneeOption.kind === "user" && assigneeOption.user)
              return (
                <div className="flex items-center gap-2 min-w-0">
                  <Avatar
                    name={assigneeOption.user.name}
                    className="text-[9px] font-black"
                    size={20}
                  />
                  <span className="text-base font-medium text-content-1000 truncate min-w-0">
                    {assigneeOption.user.name}
                  </span>
                  {assigneeOption.user.id === auth?.id ? (
                    <span className="text-base font-medium text-content-200 whitespace-nowrap shrink-0">
                      (You)
                    </span>
                  ) : null}
                </div>
              );
            return (
              <span className="block w-full truncate text-base font-medium text-content-200">
                {assigneeOption.label as string}
              </span>
            );
          }
          if (assigneeOption.kind === "user" && assigneeOption.user)
            return (
              <div className="flex items-center gap-2 py-1 min-w-0">
                <Avatar
                  name={assigneeOption.user.name}
                  className="text-[9px] font-black"
                  size={20}
                />
                <span className="text-base font-medium text-content-1000 truncate min-w-0">
                  {assigneeOption.user.name}
                </span>
                {assigneeOption.user.id === auth?.id ? (
                  <span className="text-base font-medium text-content-200 whitespace-nowrap">
                    (You)
                  </span>
                ) : null}
              </div>
            );

          return (
            <div className="flex items-center gap-2 py-1">
              <span className="text-base font-medium text-content-200">
                {assigneeOption.label as string}
              </span>
            </div>
          );
        }}
      />
    </div>
  );
};
